import { z } from "zod";

/**
 * Runtime-validated mirrors of the Python data models. Zod schemas are the
 * source of truth; TypeScript types are derived via `z.infer`. Monetary values
 * are decimal strings (parsed with decimal.js) to preserve exact precision,
 * matching `shared/schemas/output.schema.json`.
 */

const decimalString = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, "must be a decimal string")
  .nullable();

// --------------------------------------------------------------------------- //
// NormalizedRow
// --------------------------------------------------------------------------- //
export const NormalizedRowSchema = z.object({
  product_id: z.string(),
  description: z.string(),
  price: z.string(),
  cost: z.string(),
  manufacturer: z.string().nullable().default(null),
  mfg_part: z.string().nullable().default(null),
  vendor: z.string().nullable().default(null),
  vendor_sku: z.string().nullable().default(null),
  on_hand: z.number().int().default(0),
  type: z.string().nullable().default(null),
  category: z.string(),
  subcategory: z.string().nullable().default(null),
  status: z.string(),
  last_update: z.string().nullable().default(null),
  margin_dollar: decimalString.default(null),
  margin_percent: decimalString.default(null),
});
export type NormalizedRow = z.infer<typeof NormalizedRowSchema>;

// --------------------------------------------------------------------------- //
// QualityIssue
// --------------------------------------------------------------------------- //
export const SeveritySchema = z.enum(["info", "warning"]);
export type Severity = z.infer<typeof SeveritySchema>;

export const QualityIssueSchema = z.object({
  rule_id: z.string(),
  severity: SeveritySchema,
  product_id: z.string(),
  category: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).default({}),
});
export type QualityIssue = z.infer<typeof QualityIssueSchema>;

// --------------------------------------------------------------------------- //
// ProcessingResult (browser shape: in-memory, no file paths)
// --------------------------------------------------------------------------- //
export const ProcessingResultSchema = z.object({
  totalRows: z.number().int(),
  keptRows: z.number().int(),
  droppedRows: z.number().int(),
  rows: z.array(NormalizedRowSchema),
  issues: z.array(QualityIssueSchema),
});
export type ProcessingResult = z.infer<typeof ProcessingResultSchema>;

// --------------------------------------------------------------------------- //
// CatalogKitConfig (mirror of the Python config schema)
// --------------------------------------------------------------------------- //
export const AdapterTypeSchema = z.enum(["auto-detect", "connectwise", "custom"]);
export type AdapterType = z.infer<typeof AdapterTypeSchema>;

export const OutputFormatSchema = z.enum(["csv", "xlsx", "parquet", "json"]);
export const ReportFormatSchema = z.enum(["csv", "html", "json"]);

const ruleSchema = z.object({
  severity: SeveritySchema.default("warning"),
  enabled: z.boolean().default(true),
});

export const CatalogKitConfigSchema = z.object({
  adapter: z
    .object({
      type: AdapterTypeSchema.default("auto-detect"),
      custom_mapping: z.record(z.string(), z.string()).nullable().default(null),
    })
    .default({}),
  filters: z
    .object({
      status: z.string().default("Active"),
      exclude_zero_price: z.boolean().default(true),
      exclude_inactive: z.boolean().default(true),
    })
    .default({}),
  quality: z
    .object({
      enabled: z.boolean().default(true),
      rules: z.record(z.string(), ruleSchema).default({}),
      category_thresholds: z.record(z.string(), z.number()).default({}),
      low_margin_threshold_percent: z.number().default(15),
      stale_pricing_months: z.number().default(18),
    })
    .default({}),
  output: z
    .object({
      formats: z.array(OutputFormatSchema).default(["csv"]),
      split_by_category: z.boolean().default(true),
      report_formats: z.array(ReportFormatSchema).default(["csv", "html", "json"]),
    })
    .default({}),
  diff: z
    .object({
      price_change_threshold_percent: z.number().default(5),
    })
    .default({}),
});
export type CatalogKitConfig = z.infer<typeof CatalogKitConfigSchema>;

/** The fully-defaulted configuration (equivalent to `catalogkit init`). */
export const DEFAULT_CONFIG: CatalogKitConfig = CatalogKitConfigSchema.parse({});
